import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Tag,
  Avatar,
  Space,
  Button,
  Mentions,
  Timeline,
  Divider,
  Image,
  message,
  Spin,
  Typography,
  Select,
  Input,
  Modal,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCommentDots,
  faPaperclip,
  faArrowLeft,
  faPen,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { getIssueById, addIssueComment, IssueWithRelations, updateIssue, deleteIssue, getBugReportById } from '../../../services/issues';
import { getUsers } from '../../../services/users';
import dayjs from 'dayjs';
import { useAuthStore } from '../../../store/authStore';
import { colors } from '../../../theme/colors';
import { getIssueDisplayTitle } from '../issueDisplay';
import { selectActiveMentionOnTab } from '../../../utils/mentions';
import { issueReference } from '../../../utils/publicReferences';

const { Title } = Typography;
const { Option } = Select;

const statusOptions = ['BACKLOG', 'TODO', 'IN_PROGRESS', 'REVIEW', 'TESTING', 'DONE'];
const priorityOptions = ['LOWEST', 'LOW', 'MEDIUM', 'HIGH', 'HIGHEST'];

const imageExtensionPattern = /\.(jpg|jpeg|png|gif|webp|heic|heif|bmp|tif|tiff)$/i;
const videoExtensionPattern = /\.(mp4|mov|m4v|avi|webm|hevc)$/i;
const logExtensionPattern = /\.(log|txt)$/i;

const storyPointsForPriority = (priority: string) => ({
  LOWEST: 1,
  LOW: 2,
  MEDIUM: 3,
  HIGH: 5,
  HIGHEST: 8,
}[priority] || 3);

interface SelectedMention {
  id: string;
  name: string;
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const renderCommentContent = (content: string, mentionIds: string[] | undefined, users: any[]) => {
  const explicitlyMentionedNames = users
    .filter((user) => mentionIds?.includes(user.id))
    .map((user) => user.name)
    .filter(Boolean);
  const namesPresentInText = users
    .map((user) => user.name)
    .filter((name) => name && content.toLocaleLowerCase().includes(`@${name}`.toLocaleLowerCase()));
  const mentionNames = Array.from(new Set([...explicitlyMentionedNames, ...namesPresentInText]))
    .sort((a, b) => b.length - a.length);
  const mentionSource = mentionNames.length > 0
    ? `@(?:${mentionNames.map(escapeRegExp).join('|')})`
    : '(?!)';
  const tokenPattern = new RegExp(`(https?:\\/\\/[^\\s]+|${mentionSource})`, 'gi');
  const canonicalNames = new Set(mentionNames.map((name) => `@${name}`.toLowerCase()));

  return content.split(tokenPattern).map((part, index) => {
    if (/^https?:\/\//i.test(part)) {
      const url = part.replace(/[),.!?;:]+$/, '');
      const trailingPunctuation = part.slice(url.length);
      return (
        <React.Fragment key={`${part}-${index}`}>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#8EC5FF', textDecoration: 'underline', textUnderlineOffset: 2 }}>
            {url}
          </a>
          {trailingPunctuation}
        </React.Fragment>
      );
    }
    if (canonicalNames.has(part.toLowerCase())) {
      return <span key={`${part}-${index}`} style={{ color: '#F8B9E3', fontWeight: 600 }}>{part}</span>;
    }
    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
};

const renderInlineDescription = (line: string) => {
  const tokenPattern = /(\*\*[^*]+\*\*|\[[^\]]+\]\(https?:\/\/[^)]+\)|https?:\/\/[^\s]+)/g;
  return line.split(tokenPattern).map((part, index) => {
    const markdownLink = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/);
    if (markdownLink) {
      return <a key={`${part}-${index}`} href={markdownLink[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#8EC5FF', textDecoration: 'underline' }}>{markdownLink[1]}</a>;
    }
    if (/^https?:\/\//.test(part)) {
      return <a key={`${part}-${index}`} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#8EC5FF', textDecoration: 'underline' }}>{part}</a>;
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${part}-${index}`} style={{ color: colors.textPrimary }}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
};

const getAttachmentMeta = (attachment: any, index: number) => {
  const url =
    typeof attachment === 'string'
      ? attachment
      : attachment?.url || attachment?.downloadUrl || attachment?.path || '';
  const displayPath =
    typeof attachment === 'string'
      ? attachment
      : attachment?.fileName || attachment?.name || attachment?.path || url;
  const explicitType = typeof attachment === 'object' ? attachment?.type : null;
  let decodedPath = displayPath;

  try {
    const parsed = new URL(displayPath);
    decodedPath = decodeURIComponent(parsed.pathname);
  } catch {
    decodedPath = decodeURIComponent(displayPath.split('?')[0] || displayPath);
  }

  const filename = decodedPath.split('/').filter(Boolean).pop() || `Attachment ${index + 1}`;
  const isImage = explicitType === 'image' || imageExtensionPattern.test(filename);
  const isVideo = explicitType === 'video' || videoExtensionPattern.test(filename);
  const isLog = explicitType === 'log' || logExtensionPattern.test(filename);

  return {
    url,
    filename,
    isImage,
    isVideo,
    isLog,
    fileType: isImage ? 'Image' : isVideo ? 'Video' : isLog ? 'Log' : 'File',
  };
};

interface IssueDetailProps {
  issueId?: string;
  embedded?: boolean;
  onDeleted?: () => void;
  footerContainer?: HTMLElement | null;
  onResolvedReference?: (reference: string) => void;
}

const IssueDetail: React.FC<IssueDetailProps> = ({ issueId, embedded = false, onDeleted, footerContainer, onResolvedReference }) => {
  const { id: routeId } = useParams<{ id: string }>();
  const id = issueId || routeId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [commentText, setCommentText] = useState('');
  const [selectedMentions, setSelectedMentions] = useState<SelectedMention[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const { user: authUser } = useAuthStore();

  const { data: issue, isLoading } = useQuery({
    queryKey: ['issue', id],
    queryFn: () => getIssueById(id!),
    enabled: !!id,
  });
  const resolvedIssueId = issue?.id || id;

  React.useEffect(() => {
    if (!issue?.number || !id) return;
    const canonicalReference = issueReference(issue);
    if (id === canonicalReference) return;
    if (onResolvedReference) onResolvedReference(canonicalReference);
    else if (!embedded) navigate(`/queue?issue=${encodeURIComponent(canonicalReference)}`, { replace: true });
  }, [embedded, id, issue, navigate, onResolvedReference]);

  const reportId = useMemo(() => {
    const match = issue?.description?.match(/\*\*Report ID:\*\*\s*([^\s\n]+)/);
    return match?.[1] || null;
  }, [issue?.description]);

  const reportSource = useMemo<'production' | 'development'>(() => {
    const match = issue?.description?.match(/\*\*Report Source:\*\*\s*(production|development)/);
    return match?.[1] === 'development' ? 'development' : 'production';
  }, [issue?.description]);

  const { data: sourceReport } = useQuery({
    queryKey: ['bug-report-source', reportSource, reportId],
    queryFn: () => getBugReportById(reportId!, reportSource),
    enabled: !!reportId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: reportId ? 10 * 60 * 1000 : false,
  });

  const displayedDescription = useMemo(() => {
    const description = issue?.description;
    if (!description || !reportId) return description;

    // Legacy report descriptions contain a signed URL captured at issue
    // creation and a truncated Firestore log preview. Both go stale. The
    // attachment list is refreshed from Connect while the ticket stays open.
    return description
      .replace(
        /\n\*\*Attachments:\*\*\n[\s\S]*?(?=\n\*\*(?:Diagnostics|Logs):\*\*|$)/,
        '',
      )
      .replace(/\n\*\*Logs:\*\*\n```[\s\S]*?```\n?/, '');
  }, [issue?.description, reportId]);

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
  });

  const addCommentMutation = useMutation({
    mutationFn: ({ issueId, content, mentionedUserIds }: { issueId: string; content: string; mentionedUserIds: string[] }) =>
      addIssueComment(issueId, content, mentionedUserIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issue', id] });
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      setCommentText('');
      setSelectedMentions([]);
      message.success('Comment added');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to add comment');
    },
  });

  const updateIssueMutation = useMutation({
    mutationFn: ({ issueId, data }: { issueId: string; data: Record<string, any> }) =>
      updateIssue(issueId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issue', id] });
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      message.success('Issue updated');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to update issue');
    },
  });

  const deleteIssueMutation = useMutation({
    mutationFn: (issueIdToDelete: string) => deleteIssue(issueIdToDelete),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      message.success('Issue deleted');
      if (onDeleted) onDeleted();
      else navigate('/queue');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to delete issue');
    },
  });

  const canEditIssue = useMemo(() => {
    if (!authUser || !issue) {
      return false;
    }
    if (authUser.role === 'ADMIN') {
      return true;
    }
    if (authUser.role === 'VIEWER') {
      return false;
    }
    return issue.reporter?.id === authUser.id || issue.assignee?.id === authUser.id;
  }, [authUser, issue]);

  const handleIssueUpdate = (updates: Record<string, any>) => {
    if (!resolvedIssueId) return;
    updateIssueMutation.mutate({ issueId: resolvedIssueId, data: updates });
  };

  const startEditing = () => {
    if (!issue) return;
    setEditTitle(issue.title);
    setEditDescription(issue.description || '');
    setIsEditing(true);
  };

  const saveIssue = () => {
    if (!resolvedIssueId || !editTitle.trim()) return;
    updateIssueMutation.mutate(
      { issueId: resolvedIssueId, data: { title: editTitle.trim(), description: editDescription } },
      { onSuccess: () => setIsEditing(false) }
    );
  };

  const confirmDeleteIssue = () => {
    if (!resolvedIssueId) return;
    Modal.confirm({
      title: 'Delete this issue?',
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => deleteIssueMutation.mutateAsync(resolvedIssueId),
    });
  };

  const handleAddComment = () => {
    if (!resolvedIssueId || !commentText.trim()) return;
    const mentionedUserIds = selectedMentions
      .filter((mention) => commentText.toLocaleLowerCase().includes(`@${mention.name}`.toLocaleLowerCase()))
      .map((mention) => mention.id);
    addCommentMutation.mutate({ issueId: resolvedIssueId, content: commentText.trim(), mentionedUserIds });
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      BACKLOG: 'default',
      TODO: 'processing',
      IN_PROGRESS: 'warning',
      REVIEW: 'cyan',
      TESTING: 'blue',
      DONE: 'success',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      LOWEST: 'default',
      LOW: 'green',
      MEDIUM: 'orange',
      HIGH: 'red',
      HIGHEST: 'purple',
    };
    return colors[priority] || 'default';
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      BUG: 'red',
      FEATURE: 'blue',
      TASK: 'green',
      EPIC: 'purple',
      STORY: 'orange',
    };
    return colors[type] || 'default';
  };

  // Helper to render markdown-like text (simple version without react-markdown)
  const renderDescription = (text?: string) => {
    if (!text) return null;
    
    // Split by code blocks first to handle them separately
    const codeBlockRegex = /```([\s\S]*?)```/g;
    const parts: Array<{ type: 'text' | 'code'; content: string }> = [];
    let lastIndex = 0;
    let match;
    
    // Extract code blocks
    while ((match = codeBlockRegex.exec(text)) !== null) {
      // Add text before code block
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
      }
      // Add code block
      parts.push({ type: 'code', content: match[1] });
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.substring(lastIndex) });
    }
    
    // If no code blocks found, treat entire text as regular text
    if (parts.length === 0) {
      parts.push({ type: 'text', content: text });
    }
    
    return (
      <div>
        {parts.map((part, idx) => {
          if (part.type === 'code') {
            return (
              <div key={idx} style={{ margin: '16px 0' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ color: '#BFBFBF', fontSize: '12px', fontWeight: 500 }}>
                    Logs:
                  </span>
                  <Button
                    size="small"
                    onClick={() => {
                      const blob = new Blob([part.content], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `bug-report-logs-${id}-${Date.now()}.txt`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }}
                    style={{ 
                      background: '#DB50AC', 
                      borderColor: '#DB50AC',
                      color: '#F2F2F2',
                      fontSize: '11px',
                      height: '24px',
                      padding: '0 8px'
                    }}
                  >
                    Download Logs
                  </Button>
                </div>
                <pre className="queue-log-viewer" style={{
                  background: colors.bgBase,
                  border: '1px solid #333333',
                  borderRadius: '4px',
                  padding: '12px',
                  color: '#F2F2F2',
                  fontSize: '12px',
                  maxHeight: 'min(360px, 42vh)',
                  overflowX: 'auto',
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                  scrollbarGutter: 'stable both-edges',
                  whiteSpace: 'pre',
                  wordBreak: 'normal',
                  fontFamily: 'monospace',
                }}>
                  {part.content}
                </pre>
              </div>
            );
          } else {
            // Render a small safe subset of markdown without injecting HTML.
            const lines = part.content.split('\n');
            return lines.map((line, lineIdx) => {
              return (
                <p
                  key={`${idx}-${lineIdx}`}
                  style={{ color: '#BFBFBF', marginBottom: '8px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                >
                  {line ? renderInlineDescription(line) : '\u00A0'}
                </p>
              );
            });
          }
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', background: colors.bgBase, minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!issue) {
    return (
      <div style={{ padding: '24px', color: '#F2F2F2', background: colors.bgBase, minHeight: '100vh' }}>
        <p>Issue not found</p>
        <Button onClick={() => navigate('/queue')}>Back to Ops Queue</Button>
      </div>
    );
  }

  // Type assertion for issue with comments and history
  const issueWithRelations = issue as IssueWithRelations & {
    comments?: Array<{
      id: string;
      content: string;
      mentions?: string[];
      createdAt: string;
      user?: {
        id: string;
        name: string;
        email: string;
        photoUrl?: string;
      };
    }>;
    history?: Array<{
      id: string;
      action: string;
      field?: string;
      createdAt: string;
      user?: {
        id: string;
        name: string;
        email: string;
      };
    }>;
  };
  const displayAttachments = sourceReport?.attachments?.length
    ? sourceReport.attachments
    : issue.attachments || [];
  const issueActions = canEditIssue ? (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      {isEditing ? (
        <>
          <Button onClick={() => setIsEditing(false)}>Cancel</Button>
          <Button
            type="primary"
            onClick={saveIssue}
            loading={updateIssueMutation.isPending}
            disabled={!editTitle.trim()}
            style={{ background: '#DB50AC', borderColor: '#DB50AC' }}
          >
            Save
          </Button>
        </>
      ) : (
        <>
          <Button icon={<FontAwesomeIcon icon={faPen} />} onClick={startEditing}>Edit</Button>
          <Button
            danger
            icon={<FontAwesomeIcon icon={faTrash} />}
            onClick={confirmDeleteIssue}
            loading={deleteIssueMutation.isPending}
          >
            Delete
          </Button>
        </>
      )}
    </div>
  ) : null;

  return (
    <div style={{ padding: embedded ? 0 : '24px', maxWidth: embedded ? 'none' : '1200px', margin: embedded ? 0 : '0 auto', background: embedded ? 'transparent' : colors.bgBase, minHeight: embedded ? 'auto' : '100vh' }}>
      {embedded && footerContainer ? createPortal(issueActions, footerContainer) : issueActions}
      {!embedded && <Button
        icon={<FontAwesomeIcon icon={faArrowLeft} />}
        onClick={() => navigate('/queue')}
        style={{ marginBottom: '24px', background: colors.bgBase, borderColor: colors.border, color: '#F2F2F2' }}
      >
        Back
      </Button>}

      <Card
        style={{
          background: embedded ? 'transparent' : colors.bgBase,
          border: embedded ? 'none' : '1px solid #333333',
          borderRadius: embedded ? 0 : undefined,
          marginBottom: '24px',
          boxShadow: 'none',
        }}
        styles={{ body: { padding: embedded ? 0 : 24 } }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Space wrap style={{ marginBottom: '16px' }}>
              <Tag color={getTypeColor(issue.type)}>{issue.type}</Tag>
              <Tag color={getStatusColor(issue.status)}>{issue.status}</Tag>
              <Tag color={getPriorityColor(issue.priority)}>{issue.priority}</Tag>
              {reportId && (
                <Tag color={reportSource === 'development' ? 'orange' : 'blue'}>
                  {reportSource === 'development' ? 'DEV REPORT' : 'PRODUCTION REPORT'}
                </Tag>
              )}
              {reportId && sourceReport?.diagnostics && (
                <Tag color={sourceReport.diagnostics.persisted ? 'green' : 'red'}>
                  {sourceReport.diagnostics.persisted
                    ? `DIAGNOSTICS · ${sourceReport.diagnostics.eventCount || 0} EVENTS`
                    : `DIAGNOSTICS ${String(sourceReport.diagnostics.status || 'MISSING').toUpperCase()}`}
                </Tag>
              )}
              {issue.labels?.map((label, idx) => (
                <Tag key={idx}>{label}</Tag>
              ))}
            </Space>
            {isEditing ? (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Input
                  value={editTitle}
                  onChange={(event) => setEditTitle(event.target.value)}
                  placeholder="Issue title"
                  aria-label="Issue title"
                />
                <Input.TextArea
                  value={editDescription}
                  onChange={(event) => setEditDescription(event.target.value)}
                  placeholder="Issue description"
                  aria-label="Issue description"
                  rows={6}
                />
              </Space>
            ) : (
              <Title level={2} style={{ color: '#F2F2F2', margin: 0 }}>
                {getIssueDisplayTitle(issue.title, issue.description)}
              </Title>
            )}
          </div>

          {!isEditing && displayedDescription && (
            <div>
              {renderDescription(displayedDescription)}
            </div>
          )}

          <Divider style={{ borderColor: '#333333' }} />

          <Space wrap align="center">
            {issue.reporter && (
              <div>
                <span style={{ color: '#BFBFBF', marginRight: '8px' }}>Reporter:</span>
                <Avatar size="small" src={issue.reporter.photoUrl}>
                  {issue.reporter.name?.charAt(0) || issue.reporter.email?.charAt(0)}
                </Avatar>
                <span style={{ color: '#F2F2F2', marginLeft: '8px' }}>
                  {issue.reporter.name || issue.reporter.email}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#BFBFBF' }}>Assignee:</span>
              {canEditIssue ? (
                <Select
                  className="assignee-search-select"
                  value={issue.assignee?.id || 'unassigned'}
                  style={{ minWidth: 180 }}
                  onChange={(value) =>
                    handleIssueUpdate({ assigneeId: value === 'unassigned' ? null : value })
                  }
                  disabled={updateIssueMutation.isPending}
                  showSearch
                  optionFilterProp="label"
                >
                  <Option value="unassigned" label="Unassigned">
                    <Tag>Unassigned</Tag>
                  </Option>
                  {users.map((teamMember) => (
                    <Option
                      key={teamMember.id}
                      value={teamMember.id}
                      label={teamMember.name || teamMember.email}
                    >
                      <Space>
                        <Avatar size="small" src={teamMember.photoUrl}>
                          {teamMember.name?.charAt(0) || teamMember.email?.charAt(0)}
                        </Avatar>
                        <span>{teamMember.name || teamMember.email}</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              ) : issue.assignee ? (
                <Space>
                <Avatar size="small" src={issue.assignee.photoUrl}>
                  {issue.assignee.name?.charAt(0) || issue.assignee.email?.charAt(0)}
                </Avatar>
                  <span style={{ color: '#F2F2F2' }}>
                  {issue.assignee.name || issue.assignee.email}
                </span>
                </Space>
              ) : (
                <Tag>Unassigned</Tag>
              )}
              {canEditIssue && authUser && (
                <Button
                  type="link"
                  onClick={() => handleIssueUpdate({ assigneeId: authUser.id })}
                  disabled={updateIssueMutation.isPending}
                  style={{ padding: 0 }}
                >
                  Assign to me
                </Button>
              )}
              </div>
            <div>
              <span style={{ color: '#BFBFBF' }}>
                Created: {dayjs(issue.createdAt).format('MMM D, YYYY h:mm A')}
              </span>
            </div>
            <div title="Automatically assigned from priority">
              <span style={{ color: '#BFBFBF' }}>Story points: </span>
              <strong style={{ color: '#F8B9E3' }}>{storyPointsForPriority(issue.priority)}</strong>
              <span style={{ color: '#777', fontSize: 12 }}> · automatic</span>
            </div>
          </Space>

          {canEditIssue && (
            <>
              <Divider style={{ borderColor: '#333333' }} />
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 200px' }}>
                    <span style={{ color: '#BFBFBF', marginRight: '8px' }}>Status:</span>
                    <Select
                      value={issue.status}
                      onChange={(value) => handleIssueUpdate({ status: value })}
                      disabled={updateIssueMutation.isPending}
                      style={{ minWidth: 180 }}
                    >
                      {statusOptions.map((status) => (
                        <Option key={status} value={status}>
                          {status.replace('_', ' ')}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div style={{ flex: '1 1 200px' }}>
                    <span style={{ color: '#BFBFBF', marginRight: '8px' }}>Priority:</span>
                    <Select
                      value={issue.priority}
                      onChange={(value) => handleIssueUpdate({ priority: value })}
                      disabled={updateIssueMutation.isPending}
                      style={{ minWidth: 180 }}
                    >
                      {priorityOptions.map((priority) => (
                        <Option key={priority} value={priority}>
                          {priority}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </Space>
            </>
          )}

          {displayAttachments.length > 0 && (
            <div>
              <Divider style={{ borderColor: '#333333' }} />
              <div>
                <FontAwesomeIcon icon={faPaperclip} style={{ marginRight: '8px', color: '#BFBFBF' }} />
                <span style={{ color: '#BFBFBF', fontWeight: 500 }}>Attachments ({displayAttachments.length}):</span>
                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {displayAttachments.map((attachment: any, idx: number) => {
                    const { url, filename, isImage, fileType } = getAttachmentMeta(attachment, idx);
                    
                    return (
                      <div 
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '8px',
                          background: colors.bgBase,
                          borderRadius: '4px',
                          border: '1px solid #333333'
                        }}
                      >
                        {isImage ? (
                          <Image
                            src={url}
                            alt={filename}
                            width={48}
                            height={48}
                            style={{ objectFit: 'cover', borderRadius: '4px' }}
                            fallback="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=="
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faPaperclip}
                            style={{ color: '#DB50AC', fontSize: '14px' }}
                          />
                        )}
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ 
                            color: '#DB50AC', 
                            textDecoration: 'none',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={url}
                        >
                          {filename}
                        </a>
                        <Tag 
                          style={{ 
                            margin: 0,
                            background: colors.bgBase,
                            borderColor: '#333333',
                            color: '#BFBFBF'
                          }}
                        >
                          {fileType}
                        </Tag>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </Space>
      </Card>

      {/* Comments Section */}
      <Card
        title={
          <span style={{ color: '#F2F2F2' }}>
            <FontAwesomeIcon icon={faCommentDots} style={{ marginRight: '8px' }} />
            Comments ({issueWithRelations.comments?.length || 0})
          </span>
        }
        style={{
          background: embedded ? 'transparent' : colors.bgBase,
          border: embedded ? 'none' : '1px solid #333333',
          borderRadius: embedded ? 0 : undefined,
          marginBottom: '24px',
          boxShadow: 'none',
        }}
        styles={{
          header: embedded ? { paddingInline: 0 } : undefined,
          body: { padding: embedded ? '24px 0 0' : 24 },
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Mentions
            autoSize={{ minRows: 3, maxRows: 6 }}
            placeholder="Add a comment — type @ to mention someone"
            value={commentText}
            onKeyDown={selectActiveMentionOnTab}
            onChange={(value) => {
              setCommentText(value);
              setSelectedMentions((current) =>
                current.filter((mention) =>
                  value.toLocaleLowerCase().includes(`@${mention.name}`.toLocaleLowerCase())
                )
              );
            }}
            onSelect={(option: any) => {
              const selectedUser = users.find((user) => user.id === option.userId);
              if (!selectedUser) return;
              setSelectedMentions((current) =>
                current.some((mention) => mention.id === selectedUser.id)
                  ? current
                  : [...current, { id: selectedUser.id, name: selectedUser.name }]
              );
            }}
            options={users.map((user) => ({
              key: user.id,
              value: user.name,
              userId: user.id,
              label: (
                <Space size={8}>
                  <Avatar size={24} src={user.photoUrl}>{user.name.charAt(0)}</Avatar>
                  <span>{user.name}</span>
                  <span style={{ color: colors.textMuted, fontSize: 12 }}>{user.email}</span>
                </Space>
              ),
            }))}
            style={{ background: colors.bgBase, color: '#F2F2F2', borderColor: colors.border }}
          />
          <div style={{ color: colors.textMuted, fontSize: 12, marginTop: -8 }}>
            Mentioned people receive an in-app notification and an email.
          </div>
          <Button
            type="default"
            onClick={handleAddComment}
            loading={addCommentMutation.isPending}
            disabled={!commentText.trim()}
          >
            Add Comment
          </Button>

          {issueWithRelations.comments && issueWithRelations.comments.length > 0 && (
            <Timeline style={{ marginTop: '24px' }}>
              {issueWithRelations.comments.map((comment) => (
                <Timeline.Item key={comment.id} color="#DB50AC">
                  <div style={{ color: '#BFBFBF' }}>
                    <Space>
                      <Avatar size="small" src={comment.user?.photoUrl}>
                        {comment.user?.name?.charAt(0) || comment.user?.email?.charAt(0)}
                      </Avatar>
                      <strong style={{ color: '#F2F2F2' }}>
                        {comment.user?.name || comment.user?.email}
                      </strong>
                      <span style={{ color: '#666' }}>
                        {dayjs(comment.createdAt).format('MMM D, h:mm A')}
                      </span>
                    </Space>
                    <div style={{ marginTop: '8px', color: '#BFBFBF', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {renderCommentContent(comment.content, comment.mentions, users)}
                    </div>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          )}
        </Space>
      </Card>

      {/* History Section */}
      {issueWithRelations.history && issueWithRelations.history.length > 0 && (
        <Card
          title={<span style={{ color: '#F2F2F2' }}>Activity History</span>}
          style={{
            background: embedded ? 'transparent' : colors.bgBase,
            border: embedded ? 'none' : '1px solid #333333',
            borderRadius: embedded ? 0 : undefined,
            boxShadow: 'none',
          }}
          styles={{
            header: embedded ? { paddingInline: 0 } : undefined,
            body: { padding: embedded ? '24px 0 0' : 24 },
          }}
        >
          <Timeline>
            {issueWithRelations.history.map((item) => (
              <Timeline.Item key={item.id} color="#DB50AC">
                <div style={{ color: '#BFBFBF', fontSize: '12px' }}>
                  <strong style={{ color: '#F2F2F2' }}>{item.user?.name || item.user?.email}</strong>
                  {' '}
                  {item.action === 'created' && 'created this issue'}
                  {item.action === 'updated' && `updated ${item.field || 'issue'}`}
                  {' '}
                  <span style={{ color: '#666' }}>
                    {dayjs(item.createdAt).format('MMM D, h:mm A')}
                  </span>
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        </Card>
      )}
    </div>
  );
};

export default IssueDetail;
